import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FiArchive, FiRotateCcw } from 'react-icons/fi';
import {
  getBaseUrl,
  authedFetch,
  fetchStudentLocales,
  historyToMessages,
  localeToIso,
  formatLocale,
  formatLocaleShort,
  translateMessage,
  defaultStudentLocaleIso,
  archiveChat,
  unarchiveChat,
} from './api';
import { usePreferredLocale } from './PreferredLocaleContext';
import { useSpendingCap } from './SpendingCapContext';
import SpendingCapNotice from './SpendingCapNotice';
import './PersonaList.css';
import Discussion from './Discussion';
import MultiResponseSelector from './MultiResponseSelector';
import Transcript from './Transcript';
import ChatInput from './ChatInput';
import FeedbackLink from './FeedbackLink';
import './Chat.css';

// The range arithmetic moved to Transcript; kept importable from here for its callers.
export { incorrectRanges } from './Transcript';

// What this screen hangs off a turn the transcript draws: the actions offered on that
// message, whatever they are doing or failed at, and the alternatives a multi-AI
// translation returned. A component of its own because all of that is per-turn state.
function MessageAside({
  message,
  chatId,
  studentLocale,
  personaLocale,
  onSelect,
  onTranslated,
}) {
  const { t } = useTranslation();
  // Translating calls an LLM, so it goes once the allowance does. Discussing does
  // not: opening the panel only reads the thread that is already there.
  const { capped } = useSpendingCap();
  // Whether the actions menu is open, and the menu container (used to close
  // the menu when the user clicks anywhere outside of it).
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Tracks an in-flight on-demand translation so the menu item can show progress.
  const [isTranslating, setIsTranslating] = useState(false);
  // The alternative translations from a multi-AI run; null when only one model ran.
  const [translationAlternatives, setTranslationAlternatives] = useState(null);
  const [menuError, setMenuError] = useState('');

  // The translation runs in the direction the backend derives from the message's
  // author (see ChatService.translate): a student message goes from the student's
  // locale to the persona's, a persona message the other way around. When the chat's
  // locales are known we surface that direction on the Translate menu item.
  const [sourceLocale, targetLocale] =
    message.sender === 'user'
      ? [studentLocale, personaLocale]
      : [personaLocale, studentLocale];
  const translationDirection =
    sourceLocale && targetLocale
      ? `${formatLocaleShort(sourceLocale)} → ${formatLocaleShort(targetLocale)}`
      : null;

  // Close the actions menu when clicking outside of it.
  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }
    const onDocMouseDown = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  // Wraps a menu action so it closes the menu and goes no further than it.
  const runMenuAction = (fn) => (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    fn();
  };

  const handleTranslate = async () => {
    setIsTranslating(true);
    setMenuError('');
    try {
      const result = await translateMessage(chatId, message.id);
      onTranslated(message.id, result.translation);
      // Surface the tournament's alternatives (if any) in a selector card below the message.
      setTranslationAlternatives(
        result.translations && result.translations.length > 1 ? result.translations : null,
      );
    } catch (err) {
      setMenuError(t('message.translateFailed'));
    } finally {
      setIsTranslating(false);
    }
  };

  return (
    <>
      <MultiResponseSelector
        responses={translationAlternatives}
        title={t('multiResponse.translations')}
      />
      {isTranslating && <div className="message-status">{t('message.translating')}</div>}
      {menuError && <div className="error-message">{menuError}</div>}
      {message.id != null && (
        <div className="message-menu" ref={menuRef}>
          <button
            type="button"
            className="message-menu-toggle"
            title={t('message.actions')}
            aria-label={t('message.actions')}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((open) => !open);
            }}
          >
            ?
          </button>
          {menuOpen && (
            <div className="message-menu-popup" role="menu">
              <button
                type="button"
                role="menuitem"
                className="message-menu-item"
                title={t('message.discussTitle')}
                onClick={runMenuAction(() => onSelect())}
              >
                💬 {t('message.discuss')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="message-menu-item"
                title={
                  translationDirection
                    ? t('message.translateTitleWithDirection', { direction: translationDirection })
                    : t('message.translateTitle')
                }
                disabled={isTranslating || capped}
                onClick={runMenuAction(() => handleTranslate())}
              >
                {isTranslating ? `🌐 ${t('message.translating')}` : `🌐 ${t('message.translate')}`}
                {translationDirection && (
                  <span className="message-menu-item-detail">{translationDirection}</span>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Modal popup to edit a chat's settings. It loads the current values from
// /api/v0/chat/{chatId}/details (mirrors ChatDetailsDto) and saves changes
// with PATCH /api/v0/chat/{chatId}/details (mirrors ChatPatchRequest).
//
// The "I speak" dropdown lists the student locales that can be paired with the
// chat's persona, which depend on the persona's own locale (its originalLocale),
// so we fetch the persona to learn that locale before loading the options.
function ChatSettings({ chatId, onClose, onSaved }) {
  const { t } = useTranslation();
  const { preferredLocale } = usePreferredLocale();
  // Read the latest preferred locale and translator inside the loader without
  // making them effect dependencies: re-running the loader on a late profile
  // fetch or a UI-language switch would refetch and overwrite an unsaved
  // "I speak" selection.
  const preferredLocaleRef = useRef(preferredLocale);
  preferredLocaleRef.current = preferredLocale;
  const tRef = useRef(t);
  tRef.current = t;
  const [name, setName] = useState('');
  const [studentLocales, setStudentLocales] = useState([]);
  const [studentLocaleIso, setStudentLocaleIso] = useState('');
  const [translatePersonaMessages, setTranslatePersonaMessages] = useState(false);
  const [translateStudentMessages, setTranslateStudentMessages] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const detailsResponse = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}/details`, {
          headers: {
            'Accept': 'application/json',
          },
        });
        if (!detailsResponse.ok) {
          if (!cancelled) setError(tRef.current('chatSettings.loadFailed', { status: detailsResponse.status }));
          return;
        }
        // Mirrors dev.c15u.autolang.web.api.dto.chat.ChatDetailsDto
        const details = await detailsResponse.json();
        if (cancelled) return;
        setName(details.name || '');
        const savedStudentLocale = details.studentLocale
          ? localeToIso(details.studentLocale)
          : '';
        setStudentLocaleIso(savedStudentLocale);
        setTranslatePersonaMessages(!!details.translatePersonaMessages);
        setTranslateStudentMessages(!!details.translateStudentMessages);

        // The selectable student locales depend on the persona's locale.
        const personaResponse = await authedFetch(
          `${getBaseUrl()}/api/v0/persona/${details.persona.id}?include_public=true`,
          {
            headers: {
              'Accept': 'application/json',
            },
          },
        );
        if (!personaResponse.ok) {
          if (!cancelled) setError(tRef.current('chatSettings.personaLoadFailed', { status: personaResponse.status }));
          return;
        }
        // Mirrors dev.c15u.autolang.web.api.dto.persona.PersonaDto
        const persona = await personaResponse.json();
        if (cancelled) return;
        const locales = await fetchStudentLocales(localeToIso(persona.originalLocale));
        if (cancelled) return;
        setStudentLocales(locales);
        // When the chat has no student locale saved yet, default the "I speak"
        // choice to the user's preferred language (matched among the locales this
        // persona supports); otherwise keep whatever was saved.
        if (!savedStudentLocale) {
          const preferred = defaultStudentLocaleIso(locales, preferredLocaleRef.current);
          if (preferred) {
            setStudentLocaleIso(preferred);
          }
        }
      } catch (err) {
        if (!cancelled) setError(tRef.current('common.networkError'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [chatId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const studentLocale = studentLocales.find(
      (locale) => localeToIso(locale) === studentLocaleIso
    );
    setIsSaving(true);

    try {
      const response = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}/details`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        // Mirrors dev.c15u.autolang.web.api.dto.chat.ChatPatchRequest
        body: JSON.stringify({
          name,
          studentLocale: studentLocale
            ? {
                languageCode: studentLocale.languageCode,
                countryCode: studentLocale.countryCode,
              }
            : null,
          translatePersonaMessages,
          translateStudentMessages,
        }),
      });

      if (response.ok) {
        // Mirrors dev.c15u.autolang.web.api.dto.chat.ChatDetailsDto
        const updated = await response.json();
        onSaved(updated);
      } else {
        setError(t('chatSettings.saveFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="chat-settings-overlay" onClick={onClose}>
      <div
        className="chat-settings-modal"
        role="dialog"
        aria-label={t('chatSettings.title')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-settings-header">
          <h3>{t('chatSettings.title')}</h3>
          <button
            type="button"
            className="chat-settings-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            ✕
          </button>
        </div>
        {isLoading ? (
          <div className="chat-list-status">{t('chatSettings.loading')}</div>
        ) : (
          <form className="chat-settings-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="settings-name">{t('chatSettings.name')}</label>
              <input
                type="text"
                id="settings-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="settings-student-locale">{t('chatSettings.iSpeak')}</label>
              <select
                id="settings-student-locale"
                value={studentLocaleIso}
                onChange={(e) => setStudentLocaleIso(e.target.value)}
                disabled={isSaving || studentLocales.length === 0}
              >
                {studentLocales.length === 0 && (
                  <option value="">{t('chatSettings.noLanguages')}</option>
                )}
                {studentLocales.map((locale) => {
                  const iso = localeToIso(locale);
                  return (
                    <option key={iso} value={iso}>
                      {formatLocale(locale)}
                    </option>
                  );
                })}
              </select>
            </div>
            <label className="chat-settings-checkbox">
              <input
                type="checkbox"
                checked={translatePersonaMessages}
                onChange={(e) => setTranslatePersonaMessages(e.target.checked)}
                disabled={isSaving}
              />
              {t('chatSettings.translatePersona')}
            </label>
            <label className="chat-settings-checkbox">
              <input
                type="checkbox"
                checked={translateStudentMessages}
                onChange={(e) => setTranslateStudentMessages(e.target.checked)}
                disabled={isSaving}
              />
              {t('chatSettings.translateStudent')}
            </label>
            {error && <div className="error-message">{error}</div>}
            <div className="chat-settings-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={onClose}
                disabled={isSaving}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="create-button" disabled={isSaving}>
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Chat() {
  const { t } = useTranslation();
  // Sending a message runs the personae, the translator and the teacher, so it is refused
  // once the allowance is gone (#208). Reading the exchange back costs nothing and stays.
  const { capped } = useSpendingCap();
  // The chat id comes from the URL (/chats/:chatId); the chat object (for its
  // name) is passed via router state on navigation, but is absent on a hard
  // reload or deep link, in which case the header falls back to a default name.
  const { chatId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const chat = location.state?.chat;
  // The open side discussion is reflected in the URL: ?discuss=<messageId> anchors it to
  // a message, ?discuss=chat to the whole conversation. The sentinel cannot collide with
  // a message id, which is numeric.
  const [searchParams, setSearchParams] = useSearchParams();
  const discussParam = searchParams.get('discuss');
  const discussWholeChat = discussParam === 'chat';
  const discussMessageId = discussWholeChat ? null : discussParam;
  // Present only for an annotation-anchored thread; its absence means the whole message.
  const discussAnnotationParam = searchParams.get('annotation');
  const discussAnnotationIndex =
    discussAnnotationParam == null ? null : Number(discussAnnotationParam);

  /**
   * The ordered list of chat messages currently displayed in the conversation.
   * @type {[Array<{text: string, sender: 'user' | 'persona'}>, React.Dispatch<React.SetStateAction<Array<{text: string, sender: 'user' | 'persona'}>>>]}
   */
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // The index of the message currently highlighted by a click, or null.
  const [selectedIndex, setSelectedIndex] = useState(null);
  // The chat name shown in the header. Seeded from the router state and kept in
  // sync after the settings popup saves a new name. Falls back to 'Chat'.
  const [chatName, setChatName] = useState(chat?.name || t('chat.defaultName'));
  // Whether the settings popup is open.
  const [showSettings, setShowSettings] = useState(false);
  // Whether this chat is archived. Archiving only hides it from the chat list, so the
  // conversation below stays fully usable; the header is the only thing that changes.
  const [archived, setArchived] = useState(false);
  // The scene run this chat is a conversation of, or null for an ordinary chat.
  const [sceneRun, setSceneRun] = useState(null);
  // The chat's two locales, used to show each message's translation direction on
  // its Translate action. The student's is on the chat details; the persona's is
  // its originalLocale, fetched separately. Null until loaded.
  const [studentLocale, setStudentLocale] = useState(null);
  const [personaLocale, setPersonaLocale] = useState(null);
  // The scrollable message list, kept pinned to the bottom as messages arrive.
  const messagesRef = useRef(null);

  // Writes the open discussion's anchor to the URL: the message id or the whole-chat
  // sentinel under `discuss`, plus `annotation` when the thread is an annotation's.
  const setDiscuss = (discuss, annotationIndex) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('discuss', String(discuss));
      if (annotationIndex == null) {
        next.delete('annotation');
      } else {
        next.set('annotation', String(annotationIndex));
      }
      return next;
    });
  };

  // Singles the turn out and opens its discussion. The transcript hands its host the turn
  // itself, while the highlight is held by position, so the position is looked up here.
  const toggleSelected = (message) => {
    const index = messages.indexOf(message);
    setSelectedIndex((prev) => (prev === index ? null : index));
    openDiscussion(message);
  };

  // Opens the discussion panel for a message by writing ?discuss=<id> to the URL.
  const openDiscussion = (message) => {
    if (message.id == null) {
      return;
    }
    setDiscuss(message.id);
  };

  // Opens the discussion anchored to one annotation of a message. The extra `annotation`
  // parameter is what selects the ANNOTATION pointer over the MESSAGE one, mirroring the
  // nullable annotationIndex of PointerUtils.buildPointer.
  const openAnnotationDiscussion = (messageId, annotationIndex) => {
    if (messageId == null) {
      return;
    }
    setDiscuss(messageId, annotationIndex);
  };

  // Opens the discussion of the whole conversation, the thread a scene's spoken teacher
  // wrote into, mirroring PointerDto.ofChat.
  const openChatDiscussion = () => setDiscuss('chat');

  // Applies a freshly generated translation (from the actions menu's Translate item)
  // onto the matching message so the bubble shows it without reloading the history.
  const applyTranslation = (messageId, translation) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, translation } : m))
    );
  };

  // Closes the discussion panel by removing the discuss query parameter.
  const closeDiscussion = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('discuss');
      return next;
    });
  };

  // The message the open discussion is anchored to, located in the loaded
  // history by its id. Undefined until history loads (the panel still works,
  // it fetches its own thread from the chatId + messageId in the URL).
  const discussionMessage =
    discussMessageId != null
      ? messages.find((m) => String(m.id) === discussMessageId)
      : null;

  // One discussion panel is open at a time, whichever anchor holds it.
  const discussionOpen = discussWholeChat || discussionMessage != null;

  // Archiving from inside the chat changes nothing visible except the header, so there is
  // no vanishing row for a toast to compensate for: the badge shows the state and the same
  // button reverses it. A failure still needs surfacing, though.
  const toggleArchived = useCallback(async () => {
    try {
      if (archived) {
        await unarchiveChat(chatId);
        setArchived(false);
      } else {
        await archiveChat(chatId);
        setArchived(true);
      }
    } catch (err) {
      toast.error(archived ? t('chat.restoreFailed') : t('chat.archiveFailed'));
    }
  }, [archived, chatId, t]);

  const loadHistory = useCallback(async () => {
    setError('');
    setIsLoading(true);

    try {
      const response = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}`, {
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        setMessages(historyToMessages(await response.json()));
      } else {
        setError(t('chat.loadFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsLoading(false);
    }
  }, [chatId, t]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // Keep the conversation scrolled to the latest message: on the initial history
  // load, when the student submits, and when the persona's reply arrives.
  useEffect(() => {
    const el = messagesRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  // Load the chat's student and persona locales so each message can label the
  // direction its Translate action would run in. Failures are non-fatal: the
  // Translate item just falls back to a plain label.
  useEffect(() => {
    let cancelled = false;

    // Clear any locale hints from the previously viewed chat so the Translate
    // menu never shows the wrong direction while this chat's fetch is pending
    // (or if it fails).
    setStudentLocale(null);
    setPersonaLocale(null);

    const loadLocales = async () => {
      try {
        const detailsResponse = await authedFetch(
          `${getBaseUrl()}/api/v0/chat/${chatId}/details`,
          { headers: { 'Accept': 'application/json' } },
        );
        if (!detailsResponse.ok) {
          return;
        }
        // Mirrors dev.c15u.autolang.web.api.dto.chat.ChatDetailsDto
        const details = await detailsResponse.json();
        if (cancelled) return;
        setStudentLocale(details.studentLocale || null);
        setArchived(!!details.archived);
        // Carrying a run is what makes this a scene conversation and what freezes it; there is
        // no second flag here to disagree with the server's.
        setSceneRun(details.sceneRunId || null);

        const personaResponse = await authedFetch(
          `${getBaseUrl()}/api/v0/persona/${details.persona.id}?include_public=true`,
          { headers: { 'Accept': 'application/json' } },
        );
        if (!personaResponse.ok) {
          return;
        }
        // Mirrors dev.c15u.autolang.web.api.dto.persona.PersonaDto
        const persona = await personaResponse.json();
        if (cancelled) return;
        setPersonaLocale(persona.originalLocale || null);
      } catch (err) {
        // Ignore: the direction hint is optional.
      }
    };

    loadLocales();
    return () => {
      cancelled = true;
    };
  }, [chatId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const text = inputValue.trim();
    if (!text || isSending) {
      return;
    }

    setError('');
    setMessages((prev) => [...prev, { text, sender: 'user', timestamp: new Date() }]);
    setInputValue('');
    setIsSending(true);

    try {
      const response = await authedFetch(`${getBaseUrl()}/api/v0/chat/${chatId}/full`, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: text }),
      });

      if (response.ok) {
        // Mirrors dev.c15u.autolang.web.api.dto.chat.ChatUpdateFullResponse
        const data = await response.json();
        // The backend decides, from the messages actually on record, whether this one earned the
        // ask (#212). Announced app-wide because the banner is not this screen's to render, and
        // announced here so it arrives with the experience it asks about.
        if (data.feedbackInvitation) {
          window.dispatchEvent(new CustomEvent('feedback:invited'));
        }
        setMessages((prev) => {
          // Backfill the id and translation onto the student message we added optimistically.
          const updated = [...prev];
          for (let i = updated.length - 1; i >= 0; i -= 1) {
            if (updated[i].sender === 'user') {
              updated[i] = {
                ...updated[i],
                id: data.studentMessagePointer?.messageId,
                translation: data.studentTranslation,
                annotations: data.grammarAnalysis?.analysis || [],
                comment: data.shortAnswer || null,
                correction: data.grammarAnalysis?.correction || null,
                analyses: data.analyses || [],
              };
              break;
            }
          }
          return [
            ...updated,
            {
              id: data.personaMessagePointer?.messageId,
              text: data.personaAnswer,
              translation: data.personaTranslation,
              sender: 'persona',
              timestamp: new Date(),
            },
          ];
        });
      } else {
        setError(t('chat.sendFailed', { status: response.status }));
      }
    } catch (err) {
      setError(t('common.networkError'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-container">
        <div className="chat-header">
          <button onClick={() => navigate('/chats')} className="chat-back-button">
            ‹ {t('chat.back')}
          </button>
          <h2>
            {chatName}
            {archived && (
              <span className="persona-list-item-badge">{t('chat.archived')}</span>
            )}
          </h2>
          <button
            type="button"
            className="chat-settings-button"
            title={archived ? t('chat.restore') : t('chat.archive')}
            aria-label={archived ? t('chat.restore') : t('chat.archive')}
            onClick={toggleArchived}
          >
            {archived ? <FiRotateCcw aria-hidden="true" /> : <FiArchive aria-hidden="true" />}
          </button>
          <button
            type="button"
            className="chat-settings-button"
            title={t('chatSettings.title')}
            onClick={() => setShowSettings(true)}
          >
            ⚙ {t('chat.settings')}
          </button>
        </div>
        {/* The flex column the transcript's bubbles need, and what keeps them scrolled down. */}
        <div className="chat-messages" ref={messagesRef}>
          {isLoading && <div className="chat-list-status">{t('chat.loadingHistory')}</div>}
          <Transcript
            messages={messages}
            selectedIndex={selectedIndex}
            onDiscussAnnotation={openAnnotationDiscussion}
            renderAside={(message) => (
              <MessageAside
                message={message}
                chatId={chatId}
                studentLocale={studentLocale}
                personaLocale={personaLocale}
                onSelect={() => toggleSelected(message)}
                onTranslated={applyTranslation}
              />
            )}
          />
        </div>
        {error && <div className="error-message">{error}</div>}
        {capped && !sceneRun && <SpendingCapNotice />}
        {/* A record of something that happened, not a conversation to continue: the character's
            instructions assumed a situation that no longer exists. The server refuses a message
            into one, and there is no reason to offer a box that would earn a refusal. */}
        {sceneRun ? (
          <div className="chat-frozen-notice">
            {t('chat.spokenInAScene')}
            {/* Nothing to add to the record, but plenty to ask about it: the teacher spoken
                to mid-scene holds the conversation's own thread, carried on in writing. */}
            <button
              type="button"
              className="chat-ask-teacher-button"
              onClick={openChatDiscussion}
            >
              💬 {t('chat.askTeacher')}
            </button>
          </div>
        ) : (
        <form className="chat-input-form" onSubmit={handleSendMessage}>
          <ChatInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSubmit={handleSendMessage}
            placeholder={t('chat.typeMessage')}
            disabled={isSending || isLoading || capped}
          />
          <button
            type="submit"
            className="chat-send-button"
            disabled={isSending || isLoading || capped}
          >
            {isSending ? t('common.sending') : t('common.send')}
          </button>
        </form>
        )}
        {/* The discussion panel brings its own copy of the standing offer, and two of them in
            one view is the nagging a quiet link exists to avoid: while it is open, the offer
            belongs to the composer the student has moved to. */}
        {!discussionOpen && <FeedbackLink />}
      </div>
      {/* The whole-chat thread: no message anchor, no analysis, just the conversation's
          own discussion with the teacher. */}
      {discussWholeChat && <Discussion chatId={chatId} onClose={closeDiscussion} />}
      {discussionMessage && (
        <Discussion
          // Switching between a message's own thread and one of its annotations' threads
          // must remount the panel, or it would show the previous thread's history.
          key={`${discussionMessage.id}:${discussAnnotationIndex ?? 'msg'}`}
          chatId={chatId}
          messageId={discussionMessage.id}
          annotationIndex={discussAnnotationIndex}
          messageText={discussionMessage.text}
          messageTranslation={discussionMessage.translation}
          annotations={discussionMessage.annotations}
          analyses={discussionMessage.analyses}
          onClose={closeDiscussion}
        />
      )}
      {showSettings && (
        <ChatSettings
          chatId={chatId}
          onClose={() => setShowSettings(false)}
          onSaved={(details) => {
            setChatName(details.name || t('chat.defaultName'));
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
}

export default Chat;
